import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTranslation } from "react-i18next";
import { matchesCandidateSearch } from "@/utils/searchUtils";

interface Candidate {
  id: string;
  name: string;
  skills?: string[];
  match_score?: number;
  isAlreadyMatched?: boolean;
}

interface CandidateSelectorProps {
  onSelectCandidate: (candidate: Candidate) => void;
  buttonClassName?: string;
  jobId?: string; // Optional job ID to check for existing matches
}

export const CandidateSelector = ({ onSelectCandidate, buttonClassName, jobId }: CandidateSelectorProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch candidates based on search term
  useEffect(() => {
    let isActive = true;

    const fetchCandidates = async () => {
      if (open) {
        setIsLoading(true);
        setError(null);
        setCandidates([]);
        
        try {
          // Get all candidates for client-side universal search
          const { data, error } = await supabase
            .from('candidates')
            .select('id, name, skills, function, email')
            .limit(100); // Get more results for better filtering
          
          if (!isActive) return;
          
          if (error) {
            console.error("Error fetching candidates:", error);
            setError("Failed to load candidates");
            setCandidates([]);
            return;
          }
          
          // Filter candidates using universal search
          let filteredData = data || [];
          if (searchTerm) {
            filteredData = filteredData.filter(candidate => 
              matchesCandidateSearch(candidate, searchTerm)
            );
          }
          
          // Limit to 10 results after filtering
          const limitedData = filteredData.slice(0, 10);
          
          let candidatesWithMatchStatus = limitedData;
          
          // If jobId is provided, check for existing matches
          if (jobId && candidatesWithMatchStatus.length > 0) {
            const candidateIds = candidatesWithMatchStatus.map(c => c.id);
            const { data: matches, error: matchError } = await supabase
              .from('matches')
              .select('candidate_id')
              .eq('job_id', jobId)
              .in('candidate_id', candidateIds);
            
            if (!matchError && matches) {
              const matchedCandidateIds = new Set(matches.map(m => m.candidate_id));
              candidatesWithMatchStatus = candidatesWithMatchStatus.map(candidate => ({
                ...candidate,
                isAlreadyMatched: matchedCandidateIds.has(candidate.id)
              }));
            }
          }
          
          setCandidates(candidatesWithMatchStatus);
        } catch (error) {
          if (!isActive) return;
          console.error("Failed to fetch candidates:", error);
          setError("An unexpected error occurred");
          setCandidates([]);
        } finally {
          if (isActive) {
            setIsLoading(false);
          }
        }
      }
    };

    const debounceFetch = setTimeout(() => {
        fetchCandidates();
    }, 300); // 300ms debounce

    return () => {
      isActive = false;
      clearTimeout(debounceFetch);
    };
  }, [searchTerm, open, jobId]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("justify-between", buttonClassName)}
        >
          {value || t("candidates.selector.selectPlaceholder")}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <Command filter={() => 1}>
          <CommandInput 
            placeholder={t("candidates.selector.searchPlaceholder")}
            value={searchTerm}
            onValueChange={setSearchTerm}
          />
          {/* Fix: Ensure we render the CommandList with proper content */}
          <div className="max-h-[300px] overflow-y-auto" onWheel={(e) => e.stopPropagation()}>
            <CommandList>
              {isLoading ? (
                <div className="py-6 text-center text-sm text-gray-500">
                  {t("candidates.selector.loading")}
                </div>
              ) : error ? (
                <div className="py-6 text-center text-sm text-red-500">
                  {t("candidates.selector.error")}
                </div>
              ) : candidates.length === 0 ? (
                <CommandEmpty>{t("candidates.selector.noResults")}</CommandEmpty>
              ) : (
                <CommandGroup>
                  {candidates.map((candidate) => (
                    <CommandItem
                      key={candidate.id}
                      value={candidate.id}
                      disabled={candidate.isAlreadyMatched}
                      onSelect={() => {
                        if (!candidate.isAlreadyMatched) {
                          onSelectCandidate(candidate);
                          setValue(candidate.name);
                          setSearchTerm("");
                          setOpen(false);
                        }
                      }}
                      className={candidate.isAlreadyMatched ? "opacity-50 cursor-not-allowed" : ""}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === candidate.name ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className={candidate.isAlreadyMatched ? "text-gray-400" : ""}>
                            {candidate.name}
                          </span>
                          {candidate.isAlreadyMatched && (
                            <span className="text-xs text-orange-500 bg-orange-100 px-2 py-1 rounded">
                              {t("candidates.selector.alreadyMatched")}
                            </span>
                          )}
                        </div>
                        {candidate.skills && candidate.skills.length > 0 && (
                          <span className={cn(
                            "text-xs",
                            candidate.isAlreadyMatched ? "text-gray-300" : "text-gray-500"
                          )}>
                            {candidate.skills.slice(0, 3).join(", ")}
                            {candidate.skills.length > 3 && "..."}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
